import type { BillingPlan, BridgeLimits } from "../types";

/**
 * Shared plan→display mapping used by Bridge's plan pickers and the website
 * pricing page. Single source of truth so the two apps can't drift (#334).
 */

export type PlanTrack = "community" | "programmes";

/** Display/sort order for the standard Bridge tiers. */
export const PLAN_TIER_ORDER: readonly string[] = [
  "Starter",
  "Growth",
  "Established",
];

/** Normalises a plan's limits JSON, defaulting absent counts to 0. */
export function getPlanLimits(plan: BillingPlan): BridgeLimits {
  const l = plan.limits as BridgeLimits;
  return {
    groups: l.groups ?? 0,
    programmes: l.programmes ?? 0,
    facilitators: l.facilitators ?? 0,
  };
}

/**
 * Community = plan includes groups; Programmes = groups capped at 0.
 * A plan covering both groups and programmes lands under "community".
 */
export function getPlanTrack(plan: BillingPlan): PlanTrack {
  return getPlanLimits(plan).groups === 0 ? "programmes" : "community";
}

/**
 * Derives the tier label (Starter / Growth / Established) from the plan name.
 * Falls back to the full plan name for non-standard plans.
 */
export function getPlanTierLabel(plan: BillingPlan): string {
  const name = plan.name;
  if (name.includes("Starter")) {
    return "Starter";
  }
  if (name.includes("Growth")) {
    return "Growth";
  }
  if (name.includes("Established")) {
    return "Established";
  }
  return name;
}

/**
 * Semantic key used in cross-app URLs, e.g. `community-growth` /
 * `programme-starter`. Bridge's create-org plan picker pre-selects its track
 * tab via `?plan=<key>` (it checks `startsWith("programme")`), so the
 * programmes track intentionally uses the singular `programme` prefix.
 */
export function getSemanticPlanKey(plan: BillingPlan): string {
  const prefix =
    getPlanTrack(plan) === "programmes" ? "programme" : "community";
  return `${prefix}-${getPlanTierLabel(plan).toLowerCase()}`;
}
